import { html, View, type Html } from 'rune-ts';
import { PostMessageNavigate } from '../../../app/navigate';
import { dataStr } from '../../../util/dataStr';

import style from './ButtonAction.module.scss';
import { entries, pipe, map, reduce } from '@fxts/core';
import { shareFont } from '@services/MarppleShop/shared/typography';

// style_disabled: disabled 상태는 아니지만 스타일만 disabled 이고 싶을 때 사용
type ButtonActionType = 'white' | 'blue' | 'disabled' | 'line' | 'red' | 'style_disabled';
type ButtonActionSize = 'regular' | 'medium' | 'xMedium' | 'large' | 'small' | 'xSmall';

export interface ButtonActionProps {
  label: string | Html;
  submit?: boolean;
  klass?: string;
  fit?: boolean;

  attr?: Record<string, string>;
  target?: '_blank' | '_self';
  href?: string;
  type?: ButtonActionType;
  /**
   * large : 56 (mobile => 52),
   * medium : 48,
   * regular : 40 (default),
   * small : 32,
   */
  size?: ButtonActionSize;
  navigate?: PostMessageNavigate & object;
}

/**
 * @deprecated ButtonView 컴포넌트를 사용하십시오.
 */
export class ButtonAction<Props extends ButtonActionProps = ButtonActionProps> extends View<Props> {
  readonly typeOptions = {
    white: style.type_white,
    blue: style.type_blue,
    disabled: style.type_disabled,
    line: style.type_line,
    red: style.type_red,
    style_disabled: style.type_disabled,
  } as const;

  readonly sizeOptions = {
    regular: `${style.size_regular} ${shareFont('14_bold')}`,
    medium: `${style.size_medium} ${shareFont('14_bold')}`,
    xMedium: `${style.size_x_medium} ${shareFont('16_bold')}`,
    large: `${style.size_large} ${shareFont('16_bold')}`,
    small: `${style.size_small} ${shareFont('14_medium')}`,
    xSmall: `${style.size_x_small} ${shareFont('12_medium')}`,
  } as const;

  getTypeOption() {
    return (this.data.type && this.typeOptions[this.data.type]) || this.typeOptions.white;
  }

  getSizeOption() {
    return (this.data.size && this.sizeOptions[this.data.size]) || this.sizeOptions.regular;
  }

  setTypeOption(type: ButtonActionType) {
    this.data.type = type;
    this.redraw();
    return this;
  }

  setSizeOption(size: ButtonActionSize) {
    this.data.size = size;
    this.redraw();
    return this;
  }

  setLabel(label: string | Html) {
    this.data.label = label;
    return this;
  }

  override template(data: ButtonActionProps) {
    return html`
      <${data.href ? 'a' : 'button'}
        data-fit="${!!data.fit}"
        class="${style.action_button} ${data.klass ?? ''} ${this.getTypeOption()} ${this.getSizeOption()}"
        ${
          data.href
            ? html`href="${html.preventEscape(data.href)}" ${data.target ? html`target="${data.target}"` : ''}
              ${data.navigate ? html`data-post-message="${html.preventEscape(dataStr(data.navigate))}"` : ''}`
            : html`type="${data.submit ? 'submit' : 'button'}"`
        }
        ${
          data.attr
            ? pipe(
                data.attr,
                entries,
                map(([k, v]) => `${k}=${v}`),
                reduce((a, b) => `${a} ${b}`),
              )
            : ''
        }
        ${this.data.type === 'disabled' ? 'disabled' : ''}
      >
        ${this.data.label}
      </${this.data.href ? 'a' : 'button'}>
    `;
  }
}
